import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, mediaSrc, hasRichText, rewriteHtmlMedia } from "../api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/Logo";
import { ArrowLeft, Lightbulb, CheckCircle2, Save, FileText, Zap } from "lucide-react";

export default function ModuleView() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [m, setM] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [completing, setCompleting] = useState(false);

  const load = useCallback(() => {
    api.get(`/learning/modules/${moduleId}`).then((r) => {
      setM(r.data.module);
      setNotes(r.data.module.notes || "");
    }).catch(() => {});
  }, [moduleId]);

  useEffect(() => { load(); }, [load]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.post(`/learning/modules/${moduleId}/notes`, { notes });
      toast.success("Notes saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingNotes(false); }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/learning/modules/${moduleId}/complete`);
      toast.success(`Module completed! +${data.xp_awarded} XP`);
      await refresh();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setCompleting(false); }
  };

  if (!m) return <Spinner />;

  return (
    <div className="max-w-4xl mx-auto" data-testid="module-view">
      <button className="btn-ghost text-sm mb-4 -ml-2" onClick={() => navigate(`/app/learning/${m.path_id}`)}><ArrowLeft size={16} /> Back to path</button>

      <div className="flex items-center gap-2 mb-2">
        {m.completed && <span className="chip bg-emerald-500/15 text-emerald-400"><CheckCircle2 size={14} /> Completed</span>}
        <span className="chip bg-[#F26522]/10 text-[#F26522]">+{m.xp_reward} XP</span>
      </div>
      <h1 className="font-heading text-3xl font-bold" data-testid="module-view-title">{m.title}</h1>

      {m.image_url && <img src={mediaSrc(m.image_url)} alt="" className="rounded-xl mt-6 w-full object-contain max-h-72 bg-[#0B1120] border border-white/5" />}

      {m.content && (
        <div className="mt-6 card p-6 sm:p-8">
          <div className="overline mb-3">Theory</div>
          <div className="rich-content" dangerouslySetInnerHTML={{ __html: rewriteHtmlMedia(m.content) }} />
        </div>
      )}

      {hasRichText(m.examples) && (
        <div className="card p-6 sm:p-8 mt-5 border-l-2 border-[#0ea5e9]">
          <div className="flex items-center gap-2 overline mb-3"><Lightbulb size={14} /> Practical</div>
          <div className="rich-content" dangerouslySetInnerHTML={{ __html: rewriteHtmlMedia(m.examples) }} />
        </div>
      )}

      {m.key_takeaways && (
        <div className="card p-6 mt-5 bg-[#F26522]/5 border-[#F26522]/20">
          <div className="overline mb-2 text-[#F26522]">Key takeaways</div>
          <p className="text-slate-200 text-sm leading-relaxed">{m.key_takeaways}</p>
        </div>
      )}

      {/* Notes */}
      <div className="card p-6 mt-6">
        <div className="flex items-center gap-2 overline mb-3"><FileText size={14} /> My notes</div>
        <textarea className="input min-h-28 font-mono text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Jot down your notes for this module..." data-testid="module-notes" />
        <button className="btn-secondary mt-3 text-sm" onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn"><Save size={15} /> Save notes</button>
      </div>

      {/* Action */}
      <div className="mt-8 flex items-center justify-between card p-6 sticky bottom-4 glass">
        {m.quiz ? (
          <>
            <div>
              <div className="font-semibold flex items-center gap-2"><Zap size={16} className="text-[#F26522]" /> Module assessment</div>
              <p className="text-slate-400 text-sm mt-0.5">Pass the quiz ({m.quiz.pass_threshold}%+) to complete this module.</p>
            </div>
            <button className="btn-primary" onClick={() => navigate(`/app/quiz/${m.quiz.id}`)} data-testid="take-quiz-btn">Take quiz</button>
          </>
        ) : (
          <>
            <div>
              <div className="font-semibold">Finished reading?</div>
              <p className="text-slate-400 text-sm mt-0.5">Mark this module complete to earn XP.</p>
            </div>
            <button className="btn-primary" onClick={complete} disabled={completing || m.completed} data-testid="complete-module-btn">
              <CheckCircle2 size={18} /> {m.completed ? "Completed" : "Mark complete"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
